import { useContext, useEffect, useCallback, useState, useRef } from 'react';
import { useIntl } from 'react-intl';
import { audioMessages } from './i18n';
import { ISubmitContextState } from '../../../contexts/submit.context';
import {
	IChatMessagesOut,
	IChatVoiceMessage,
	IFileResource,
	MessageType,
	Resource,
} from '@enuan/chat__utils';

import { configContext } from '../../../contexts/config/context';
import { v4 as uuidV4 } from 'uuid';

export const useRecorder = (
	onError: (error: ISubmitContextState['error']) => void,
) => {
	const recorder = useRef<MediaRecorder>(undefined);
	const { setIsRecordAudioApproved } = useContext(configContext);
	const intl = useIntl();
	const permission_error = intl.formatMessage(audioMessages.permission_error);

	const [isRecording, setIsRecording] = useState<boolean>(false);

	const mediaDevices = navigator.mediaDevices;
	const isSecured = !!mediaDevices;
	const isSupported = isSecured && !!mediaDevices?.getUserMedia;

	const stop = useCallback(
		(fileName?: string) =>
			new Promise<File>((resolve, reject) => {
				try {
					if (isRecording && recorder.current) {
						recorder.current.addEventListener('dataavailable', ({ data }) => {
							const file = new File([data], `${fileName ?? uuidV4()}.mp3`, {
								type: data.type,
								lastModified: Date.now(),
							});
							setIsRecording(false);
							resolve(file);
						});
						recorder.current?.stop();
						recorder.current?.stream.getTracks().forEach((i) => i.stop());
						console.log('Stopping recording');
					}
				} catch (e) {
					reject(e);
				}
			})
				.then(Resource)
				.then((resource: any) => {
					console.log('Setting recorded message');
					const msg: IChatMessagesOut<IChatVoiceMessage<IFileResource>> = {
						_type: MessageType.VOICE,
						_data: resource,
					};
					return msg;
				}),
		[isRecording],
	);

	const start = useCallback(
		() =>
			new Promise<void>((resolve, reject) => {
				recorder.current = undefined;
				try {
					if (!isRecording && isSupported) {
						navigator.mediaDevices
							.getUserMedia({ audio: true })
							.then((stream) => {
								console.log('Recording message');
								setIsRecordAudioApproved(true);
								recorder.current = new MediaRecorder(stream);

								// Start recording
								recorder.current.start();
								setIsRecording(true);
								resolve();
							});
					} else if (!isSupported) {
						throw new Error('Not supported');
					} else {
						throw new Error('Is recording');
					}
				} catch (e) {
					reject(e);
					onError(permission_error);
				}
			}),
		// ,
		[
			isRecording,
			isSupported,
			permission_error,
			onError,
			setIsRecordAudioApproved,
		],
	);

	useEffect(() => {
		if (!isSupported) {
			onError(permission_error);
		}
	}, [isSupported, permission_error, onError]);

	return { start, stop, isRecording, isSecured, isSupported };
};
